import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FixedSizeList } from 'react-window';
import {
  Box,
  Typography,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import Card from './Card';
import Button from './Button';
import Loading from './Loading';
import Alert from './Alert';
import EmptyState from './EmptyState';
import api from '../services/api';

// Virtualized-table layout. Onboarding runs can leave 5000+ rows in this
// list — a plain (non-virtualized) MUI Table mounts every row's DOM/MUI
// subcomponents at once, which is the dominant rendering cost once a large
// batch is sitting in a settled (non-pending) state. `FixedSizeList` only
// mounts rows near the visible viewport, so this table intentionally does
// NOT use the shared `Table` component (app/frontend/src/components/
// Table.js) — that component is a real `<table>` used by several other
// pages, and real `<table>` layout doesn't work with react-window's
// absolutely-positioned rows. Column alignment is done with a CSS Grid
// template shared by the header and every row instead.
const SKILLS_COLUMNS = [
  { key: 'retailer', label: 'Retailer', minWidth: 140 },
  { key: 'pageType', label: 'Page Type', minWidth: 130 },
  { key: 'attribute', label: 'Attribute', minWidth: 140 },
  { key: 'configFile', label: 'Config File', width: 280 },
  { key: 'owner', label: 'Owner', width: 180 },
  { key: 'status', label: 'Status', width: 120 },
  { key: 'generatedAt', label: 'Generated At', width: 160 },
  { key: 'actions', label: 'Actions', width: 120, align: 'right' },
];
const SKILLS_GRID_TEMPLATE = SKILLS_COLUMNS
  .map((col) => (col.width ? `${col.width}px` : `minmax(${col.minWidth}px, 1fr)`))
  .join(' ');
// Sum of the columns' minimums — below this, columns can't shrink further,
// so the grid overflows its container and the row/header need to scroll
// horizontally together (see StickyHeaderInner) rather than clip.
const SKILLS_TABLE_MIN_WIDTH = SKILLS_COLUMNS.reduce((sum, col) => sum + (col.width ?? col.minWidth), 0);
const SKILLS_ROW_HEIGHT = 57; // matches the ~16px cell padding + line height the shared Table used
const SKILLS_HEADER_HEIGHT = 60;
const SKILLS_LIST_MAX_HEIGHT = 640; // ~11 rows visible before the list scrolls internally

const skillsCellSx = {
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  fontSize: '0.9rem',
  color: '#cbd5e1',
  px: 2,
};

// Status filter values recognized by the healing agent's skills API.
const STATUS_FILTERS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'generated', label: 'Generated' },
  { value: 'edited', label: 'Edited' },
  { value: 'error', label: 'Error' },
];

const PAGE_TYPE_FILTERS = [
  { value: 'detail', label: 'Detail Page' },
  { value: 'search', label: 'Search Page' },
  { value: 'category', label: 'Category Page' },
  { value: 'store', label: 'Store Page' },
];

const EMPTY_FILTERS = {
  status: '',
  retailer: '',
  attribute: '',
  pageType: '',
  configFilePath: '',
};

const STATUS_COLORS = {
  pending: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  generated: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
  edited: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
};

function StatusChip({ status }) {
  if (!status) return <Typography sx={{ color: '#94a3b8' }}>—</Typography>;
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        background: STATUS_COLORS[status] || 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
        color: 'white',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}
    />
  );
}

function pageTypeLabel(value) {
  const key = value || 'detail';
  const known = PAGE_TYPE_FILTERS.find((option) => option.value === key);
  return known ? known.label : key;
}

function skillConfigFilePath(skill) {
  return skill?.configFilePath || skill?.config_file_path || '';
}

// Shared row identity — used for the table's `key` prop and to diff the
// pending-status poll below against the currently-held list.
function skillKey(skill) {
  return skill.id ?? `${skill.retailer}-${skill.attribute}-${skill.pageType}`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

// react-window's FixedSizeList only renders the header-less list of rows —
// it doesn't know about a header row. `innerElementType` is react-window's
// documented extension point for exactly this: we prepend a real (non
// virtualized) header here and offset every row's absolute `top` by its
// height in SkillRow below. Rendering the header INSIDE react-window's own
// scrolling element (rather than as a sibling above it) is what keeps it in
// sync when the table scrolls horizontally on a narrow viewport — a header
// rendered outside that scroll container would not move with it.
const SkillsTableInner = React.forwardRef(function SkillsTableInner({ children, style, ...rest }, ref) {
  return (
    <div ref={ref} style={{ ...style, height: `${parseFloat(style.height) + SKILLS_HEADER_HEIGHT}px` }} {...rest}>
      <Box
        role="row"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          minWidth: SKILLS_TABLE_MIN_WIDTH,
          display: 'grid',
          gridTemplateColumns: SKILLS_GRID_TEMPLATE,
          height: SKILLS_HEADER_HEIGHT,
        }}
        sx={{
          alignItems: 'center',
          background: '#1e293b',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        {SKILLS_COLUMNS.map((col) => (
          <Box
            key={col.key}
            role="columnheader"
            sx={{
              px: 2,
              color: '#e2e8f0',
              fontWeight: 700,
              fontSize: '0.9rem',
              textAlign: col.align || 'left',
            }}
          >
            {col.label}
          </Box>
        ))}
      </Box>
      {children}
    </div>
  );
});

const SkillRow = React.memo(function SkillRow({ index, style, data }) {
  const { skills: rows, onRowClick, onDelete, deletingKey } = data;
  const skill = rows[index];
  const configPath = skillConfigFilePath(skill);
  const key = skillKey(skill);
  const isDeleting = deletingKey === key;

  return (
    <Box
      role="row"
      style={{
        ...style,
        top: `${parseFloat(style.top) + SKILLS_HEADER_HEIGHT}px`,
        minWidth: SKILLS_TABLE_MIN_WIDTH,
        display: 'grid',
        gridTemplateColumns: SKILLS_GRID_TEMPLATE,
      }}
      onClick={() => onRowClick(skill)}
      sx={{
        alignItems: 'center',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        cursor: 'pointer',
        '&:hover': { background: 'rgba(59, 130, 246, 0.05)' },
      }}
    >
      <Box role="cell" sx={skillsCellSx}>
        <Typography sx={{ color: '#e2e8f0', fontWeight: 500 }} noWrap>{skill.retailer}</Typography>
      </Box>
      <Box role="cell" sx={skillsCellSx}>{pageTypeLabel(skill.pageType)}</Box>
      <Box role="cell" sx={skillsCellSx}>{skill.attribute}</Box>
      <Box role="cell" sx={skillsCellSx}>
        {configPath ? (
          <Tooltip title={configPath} placement="top">
            <Box
              component="span"
              sx={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                color: '#94a3b8',
              }}
            >
              {configPath}
            </Box>
          </Tooltip>
        ) : (
          <Typography sx={{ color: '#94a3b8' }}>—</Typography>
        )}
      </Box>
      <Box role="cell" sx={skillsCellSx}>
        {skill.configOwner || skill.parentRetailer ? (
          <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }} noWrap>
            {skill.configOwner ? `Owner: ${skill.configOwner}` : null}
            {skill.configOwner && skill.parentRetailer ? ' · ' : ''}
            {skill.parentRetailer ? `Parent: ${skill.parentRetailer}` : null}
          </Typography>
        ) : (
          <Typography sx={{ color: '#94a3b8' }}>—</Typography>
        )}
      </Box>
      <Box role="cell" sx={skillsCellSx}>
        <StatusChip status={skill.status} />
      </Box>
      <Box role="cell" sx={skillsCellSx}>
        <Typography sx={{ fontSize: '0.875rem', color: '#94a3b8' }} noWrap>
          {formatDate(skill.generatedAt)}
        </Typography>
      </Box>
      <Box role="cell" sx={{ ...skillsCellSx, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
        <Button
          variant="outlined"
          color="error"
          size="small"
          disabled={isDeleting}
          onClick={(e) => onDelete(e, skill)}
        >
          {isDeleting ? 'Deleting…' : 'Delete'}
        </Button>
      </Box>
    </Box>
  );
});

function SkillsListPage() {
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [deletingKey, setDeletingKey] = useState(null);

  // Guards against an out-of-order response overwriting a newer fetch.
  const requestSeq = useRef(0);

  const loadSkills = useCallback(async ({ silent } = {}) => {
    const requestId = ++requestSeq.current;
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const data = await api.getSkills();
      if (requestId !== requestSeq.current) return;
      setSkills(Array.isArray(data) ? data : []);
    } catch (err) {
      if (requestId !== requestSeq.current) return;
      setError(err.message || 'Failed to load skills');
      setSkills([]);
    } finally {
      if (requestId === requestSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSkills();
  }, [loadSkills]);

  const filterOptions = useMemo(() => {
    const retailers = [...new Set(skills.map((skill) => skill.retailer).filter(Boolean))].sort();
    const attributes = [...new Set(skills.map((skill) => skill.attribute).filter(Boolean))].sort();
    const knownOrder = PAGE_TYPE_FILTERS.map((opt) => opt.value);
    const pageTypes = [...new Set([
      ...knownOrder,
      ...skills.map((skill) => skill.pageType || 'detail'),
    ])].sort((a, b) => {
      const aIdx = knownOrder.indexOf(a);
      const bIdx = knownOrder.indexOf(b);
      if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
    const configFilePaths = [...new Set(skills.map(skillConfigFilePath).filter(Boolean))].sort();
    return { retailers, attributes, pageTypes, configFilePaths };
  }, [skills]);

  const filteredSkills = useMemo(() => {
    return skills.filter((skill) => {
      if (filters.status && skill.status !== filters.status) return false;
      if (filters.retailer && skill.retailer !== filters.retailer) return false;
      if (filters.attribute && skill.attribute !== filters.attribute) return false;
      if (filters.pageType && (skill.pageType || 'detail') !== filters.pageType) return false;
      if (filters.configFilePath && skillConfigFilePath(skill) !== filters.configFilePath) return false;
      return true;
    });
  }, [skills, filters]);

  const hasActiveFilters = Object.values(filters).some(Boolean);
  const clearFilters = () => setFilters(EMPTY_FILTERS);

  // Mirrors `skills` for the poll below to read without needing `skills` in
  // its effect deps — that would tear down and restart the interval on
  // every unrelated skills-state change (e.g. a delete).
  const skillsRef = useRef(skills);
  useEffect(() => { skillsRef.current = skills; }, [skills]);

  const hasPending = skills.some((skill) => skill.status === 'pending');
  // Tracks the set of currently-pending skill keys (see skillKey above) so
  // each tick can tell "nothing finished yet" from "something changed"
  // without touching the full 5000+-row `skills` array. Onboarding a large
  // batch means most rows start out pending, and a naive poll that always
  // calls setSkills(freshFullList) — replacing the array reference every
  // 3s — re-triggers both list-page useMemos and a full re-render of every
  // row indefinitely, for as long as anything is still pending. Polling
  // just the (usually much smaller) pending subset and only calling
  // loadSkills when that set actually changed skips that re-render on every
  // tick where nothing happened, which is most ticks during a long batch.
  const pendingKeysRef = useRef(null);
  useEffect(() => {
    if (!hasPending) {
      pendingKeysRef.current = null;
      return undefined;
    }
    if (pendingKeysRef.current === null) {
      pendingKeysRef.current = new Set(
        skillsRef.current.filter((skill) => skill.status === 'pending').map(skillKey)
      );
    }
    const intervalId = setInterval(async () => {
      let pendingNow;
      try {
        pendingNow = await api.getSkills('pending');
      } catch {
        return; // transient failure — try again next tick
      }
      // Filter defensively rather than trusting the `?status=pending` query
      // param was honored server-side — if it's ever ignored upstream and
      // this trusted the raw response, newKeys would silently become "every
      // skill" on the first tick and stay that way forever after, since it
      // would then always equal itself and the poll would stop refreshing
      // statuses while still polling.
      const newKeys = new Set(
        (Array.isArray(pendingNow) ? pendingNow : [])
          .filter((skill) => skill.status === 'pending')
          .map(skillKey)
      );
      const prevKeys = pendingKeysRef.current;
      const unchanged = prevKeys.size === newKeys.size && [...prevKeys].every((k) => newKeys.has(k));
      if (unchanged) return;
      pendingKeysRef.current = newKeys;
      loadSkills({ silent: true });
    }, 3000);
    return () => clearInterval(intervalId);
  }, [hasPending, loadSkills]);

  const handleRowClick = useCallback((skill) => {
    // Carry pageType through as a query param — the editor defaults to
    // 'detail' when it's absent, which would silently point a 'search'
    // pageType row at the wrong (detail) skill.
    const pageType = skill.pageType || 'detail';
    navigate(
      `/skills/${encodeURIComponent(skill.retailer)}/${encodeURIComponent(skill.attribute)}?pageType=${encodeURIComponent(pageType)}`,
    );
  }, [navigate]);

  const handleDelete = useCallback(async (event, skill) => {
    event.stopPropagation();
    const label = `${skill.retailer} / ${skill.attribute}`;
    if (!window.confirm(`Delete the skill for ${label}? This cannot be undone.`)) {
      return;
    }
    setDeletingKey(skillKey(skill));
    setError(null);
    try {
      await api.deleteSkill(skill.retailer, skill.attribute, skill.pageType || 'detail');
      await loadSkills();
    } catch (err) {
      setError(err.message || 'Failed to delete skill');
    } finally {
      setDeletingKey(null);
    }
  }, [loadSkills]);

  // Stable reference so SkillRow's React.memo actually skips re-rendering
  // off-screen rows when unrelated state (e.g. a filter dropdown) changes.
  const rowData = useMemo(
    () => ({ skills: filteredSkills, onRowClick: handleRowClick, onDelete: handleDelete, deletingKey }),
    [filteredSkills, handleRowClick, handleDelete, deletingKey],
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
          Skills
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            component={Link}
            to="/skills/onboard/csv"
          >
            Bulk upload CSV
          </Button>
          <Button
            startIcon={<AddIcon />}
            component={Link}
            to="/skills/onboard"
          >
            Onboard
          </Button>
        </Stack>
      </Stack>

      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}

      <Card sx={{ mb: 2 }} contentSx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterListIcon sx={{ color: '#64748b' }} />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))}
            >
              {STATUS_FILTERS.map((option) => (
                <MenuItem key={option.value || 'all'} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Retailer</InputLabel>
            <Select
              value={filters.retailer}
              label="Retailer"
              onChange={(e) => setFilters((current) => ({ ...current, retailer: e.target.value }))}
            >
              <MenuItem value="">All retailers</MenuItem>
              {filterOptions.retailers.map((retailer) => (
                <MenuItem key={retailer} value={retailer}>{retailer}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Attribute</InputLabel>
            <Select
              value={filters.attribute}
              label="Attribute"
              onChange={(e) => setFilters((current) => ({ ...current, attribute: e.target.value }))}
            >
              <MenuItem value="">All attributes</MenuItem>
              {filterOptions.attributes.map((attribute) => (
                <MenuItem key={attribute} value={attribute}>{attribute}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Page Type</InputLabel>
            <Select
              value={filters.pageType}
              label="Page Type"
              onChange={(e) => setFilters((current) => ({ ...current, pageType: e.target.value }))}
            >
              <MenuItem value="">All page types</MenuItem>
              {filterOptions.pageTypes.map((pageType) => {
                const known = PAGE_TYPE_FILTERS.find((option) => option.value === pageType);
                return (
                  <MenuItem key={pageType} value={pageType}>
                    {known ? known.label : pageType}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Config File</InputLabel>
            <Select
              value={filters.configFilePath}
              label="Config File"
              onChange={(e) => setFilters((current) => ({ ...current, configFilePath: e.target.value }))}
            >
              <MenuItem value="">All config files</MenuItem>
              {filterOptions.configFilePaths.map((path) => (
                <MenuItem key={path} value={path}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', maxWidth: 420 }} noWrap>
                    {path}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Tooltip title="Clear filters">
              <IconButton size="small" onClick={clearFilters}>
                <ClearIcon sx={{ color: '#64748b' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Card>

      <Card title={`Skills (${filteredSkills.length})`} gradient>
        {loading ? (
          <Loading message="Loading skills..." />
        ) : filteredSkills.length === 0 ? (
          <EmptyState
            message={hasActiveFilters ? 'No skills match these filters' : 'No skills onboarded yet'}
            description={hasActiveFilters ? undefined : 'Onboard a retailer + attribute to get started.'}
          />
        ) : (
          <Box
            role="table"
            sx={{
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              background: '#0f172a',
              overflow: 'hidden',
            }}
          >
            <FixedSizeList
              height={Math.min(filteredSkills.length * SKILLS_ROW_HEIGHT, SKILLS_LIST_MAX_HEIGHT) + SKILLS_HEADER_HEIGHT}
              width="100%"
              itemCount={filteredSkills.length}
              itemSize={SKILLS_ROW_HEIGHT}
              itemData={rowData}
              itemKey={(index, data) => skillKey(data.skills[index])}
              innerElementType={SkillsTableInner}
              style={{ overflowX: 'auto' }}
            >
              {SkillRow}
            </FixedSizeList>
          </Box>
        )}
      </Card>
    </Box>
  );
}

export default SkillsListPage;
